import { useState, useEffect } from 'react';
import { 
  MALE_LIFE_EXPECTANCY,
  FEMALE_LIFE_EXPECTANCY,
  WEEKS_IN_YEAR,
  DAYS_IN_YEAR,
  HOURS_IN_DAY,
  MINUTES_IN_HOUR,
  HEARTBEATS_PER_MINUTE,
  MEALS_PER_DAY,
  SLEEP_HOURS_PER_DAY,
  PAGES_IN_BOOK,
  SEASONS
} from '../utils/constants';

// Define the stats state type
export interface LifeStats {
  totalWeeks: number;
  weeksLived: number;
  yearsLived: number;
  monthsLived: number;
  currentWeek: number;
  currentYear: number;
  currentSeason: string;
  progressPercent: number;
  lifeExpectancy: number;
  heartbeatsLived: number;
  totalHeartbeats: number;
  breathsLived: number;
  moonPhasesLived: number;
  sleepHours: number;
  mealsEaten: number;
  sunrisesSeen: number;
  pagesRead: number;
  pagesInLifeBook: number;
}

export const useLifeStats = (birthdate: string, sex: string) => {
  const [stats, setStats] = useState<LifeStats>({
    totalWeeks: 0,
    weeksLived: 0,
    yearsLived: 0,
    monthsLived: 0,
    currentWeek: 0,
    currentYear: 0,
    currentSeason: "",
    progressPercent: 0,
    lifeExpectancy: 0,
    heartbeatsLived: 0,
    totalHeartbeats: 0,
    breathsLived: 0,
    moonPhasesLived: 0,
    sleepHours: 0,
    mealsEaten: 0,
    sunrisesSeen: 0,
    pagesRead: 0,
    pagesInLifeBook: 0
  });

  // Calculate life statistics based on inputs
  useEffect(() => {
    if (!birthdate) return;

    const today = new Date();
    const birth = new Date(birthdate);

    // Calculate life expectancy based on sex
    const lifeExpectancy = sex === "male" ? MALE_LIFE_EXPECTANCY : FEMALE_LIFE_EXPECTANCY;

    // Calculate basic metrics
    const msLived = today.getTime() - birth.getTime();
    const daysLived = msLived / (1000 * 60 * 60 * 24);
    const weeksLived = daysLived / 7;
    const yearsLived = daysLived / DAYS_IN_YEAR;
    const monthsLived = daysLived / (DAYS_IN_YEAR / 12);

    // Calculate additional metrics
    const hoursLived = daysLived * HOURS_IN_DAY;
    const minutesLived = hoursLived * MINUTES_IN_HOUR;
    const heartbeatsLived = minutesLived * HEARTBEATS_PER_MINUTE;
    const breathsPerMinute = 16; // Average
    const breathsLived = minutesLived * breathsPerMinute;
    const moonPhasesLived = Math.floor(daysLived / 29.53);
    const mealsEaten = daysLived * MEALS_PER_DAY;
    const sleepHours = daysLived * SLEEP_HOURS_PER_DAY;
    const sunrisesSeen = daysLived;
    const pagesInLifeBook = lifeExpectancy * PAGES_IN_BOOK;
    const pagesRead = yearsLived * PAGES_IN_BOOK;

    // Totals
    const totalWeeks = lifeExpectancy * WEEKS_IN_YEAR;
    const totalHeartbeats = lifeExpectancy * DAYS_IN_YEAR * HOURS_IN_DAY * MINUTES_IN_HOUR * HEARTBEATS_PER_MINUTE;

    // Current progress
    const progressPercent = lifeExpectancy > 0 ? (yearsLived / lifeExpectancy) * 100 : 0;

    // Determine current season of life
    const currentYear = Math.floor(yearsLived);
    const currentSeason = SEASONS.find(
      season => currentYear >= season.start && currentYear <= season.end
    )?.name || "Winter"; // Default to Winter if not found

    setStats({
      totalWeeks: Math.round(totalWeeks),
      weeksLived: Math.floor(weeksLived),
      yearsLived: parseFloat(yearsLived.toFixed(2)),
      monthsLived: Math.floor(monthsLived),
      currentWeek: Math.floor(weeksLived % WEEKS_IN_YEAR),
      currentYear,
      currentSeason,
      progressPercent: parseFloat(progressPercent.toFixed(2)),
      lifeExpectancy: parseFloat(lifeExpectancy.toFixed(1)),
      heartbeatsLived: Math.floor(heartbeatsLived),
      totalHeartbeats: Math.round(totalHeartbeats),
      breathsLived: Math.floor(breathsLived),
      moonPhasesLived,
      sleepHours: Math.floor(sleepHours),
      mealsEaten: Math.floor(mealsEaten),
      sunrisesSeen: Math.floor(sunrisesSeen),
      pagesRead: Math.floor(pagesRead),
      pagesInLifeBook: Math.round(pagesInLifeBook)
    });
  }, [birthdate, sex]);

  // Helper function to get current season data
  const getCurrentSeason = () => {
    if (!stats.currentYear) return null;
    return SEASONS.find(season => 
      stats.currentYear >= season.start && stats.currentYear <= season.end
    );
  };

  return {
    stats,
    getCurrentSeason
  };
}; 